import { forwardRef, useImperativeHandle } from 'react';

import { Box, Grid, Skeleton } from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { logger } from '@/utils';

import { updatePaymentMethod } from '../../api';
import { useSetupIntent } from '../../hooks';

const stripePromise = loadStripe(ENV.STRIPE_PUBLIC);

interface CardDetailsFormProps {
  companyID: string;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

export interface CardDetailsFormRef {
  submitForm: () => void;
}

const CardDetailsFormInner = forwardRef<
  CardDetailsFormRef,
  CardDetailsFormProps
>(({ companyID, onSuccess, onError }, ref) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      const msg = error.message ?? 'Something went wrong.';
      onError(msg);
    } else {
      try {
        await updatePaymentMethod(companyID, setupIntent?.payment_method);
        const msg = 'Card updated successfully!';
        onSuccess(msg);
      } catch {
        const msg = 'Failed to update card.';
        onError(msg);
      }
    }
  };

  useImperativeHandle(ref, () => ({ submitForm: handleSubmit }));

  return (
    <div className="space-y-4 max-w-md">
      <PaymentElement />
    </div>
  );
});

CardDetailsFormInner.displayName = 'CardDetailsFormInner';

export const CardDetailsForm = forwardRef<
  { submitForm: () => void },
  CardDetailsFormProps
>(({ companyID, onSuccess, onError }, ref) => {
  const { data: clientSecret, isLoading, error } = useSetupIntent(companyID);

  if (error) {
    logger.error('Failed to fetch SetupIntent', error);
    onError('Failed to load Stripe setup.');
    return null;
  }

  if (!clientSecret || isLoading) {
    return (
      <Box sx={{ border: '1px solid #ebeded', borderRadius: 1, px: 2, py: 3 }}>
        <Skeleton height={64} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Skeleton height={64} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton height={64} />
          </Grid>
        </Grid>
        <Skeleton height={64} />
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardDetailsFormInner
        ref={ref}
        companyID={companyID}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
});

CardDetailsForm.displayName = 'CardDetailsForm';
